import { getFromLocalStorage, setInLocalStorage } from "./chunk-YLGZRZS2.js";
import { __async, __spreadProps, __spreadValues } from "./chunk-6FNC3XMI.js";

// src/react/error-management.ts
import { customAlphabet } from "nanoid";
import * as Sentry from "@sentry/browser";
var debugId;
var debugIdLocalStorageName;
var isInitialized = false;
var isDebugEnabled = false;
var isAxiosError = exception2 => typeof exception2 === "object" && exception2 !== null && "isAxiosError" in exception2;
var isBlockedGoogleAdBreadcrumb = breadcrumb => {
  const {
    category,
    data,
    level
  } = breadcrumb;
  return category === "fetch" && level === "error" && (data == null ? void 0 : data.method) === "GET" && data.url === "https://pagead2.googlesyndication.com/pagead/buyside_topics/set/";
};
var isXhrWithoutResponseBreadcrumb = breadcrumb => {
  const {
    category,
    data
  } = breadcrumb;
  return category === "xhr" && (data == null ? void 0 : data.status_code) === 0;
};
var writeDebugLog = (message, extra) => {
  if (isDebugEnabled) {
    console.log("[Printdeal Error Management] ".concat(message), extra);
  }
};
var getDebugId = () => {
  if (debugId) {
    return debugId;
  }
  if (debugIdLocalStorageName) {
    debugId = getFromLocalStorage(debugIdLocalStorageName);
    if (debugId) {
      return debugId;
    }
  }
  const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
  const nanoid = customAlphabet(alphabet, 10);
  debugId = nanoid();
  if (debugIdLocalStorageName) {
    setInLocalStorage(debugIdLocalStorageName, debugId);
  }
  return debugId;
};
var init2 = params => {
  debugIdLocalStorageName = params.debugIdLocalStorageName;
  isDebugEnabled = Boolean(params.debug);
  Sentry.init({
    dsn: params.url,
    release: params.release,
    environment: params.environment,
    debug: isDebugEnabled,
    maxBreadcrumbs: 50,
    autoSessionTracking: true,
    normalizeDepth: 6,
    integrations: [Sentry.dedupeIntegration(), Sentry.extraErrorDataIntegration(), Sentry.browserTracingIntegration(), Sentry.replayIntegration({
      blockAllMedia: false,
      maskAllInputs: true,
      maskAllText: true,
      block: ["*[data-hj-suppress]", ".data-sentry-block"],
      useCompression: true,
      networkDetailAllowUrls: [/\/page-data.*\.json/]
    })],
    ignoreErrors: [...(params.ignoreErrors || []), "ResizeObserver loop limit exceeded", "ResizeObserver loop completed with undelivered notifications", "CookieControl", "webkitPresentationMode", "iFrameResizer", "ChunkLoadError", "Pastease", "UET is not defined"],
    denyUrls: [/accutics\.net/, /bing\.com/, /cookiebot\.com/, /criteo\.com/, /doubeclick\.net/, /facebook\.net/, /google\.com/, /google-analytics\.com/, /googleadservices\.com/, /googlesyndication\.com/, /googletagmanager\.com/, /hotjar\.com/, /hs-analytics\.com/, /mopinion\.com/, /omappapi\.com/, /opmnstr\.com/, /segment\.com/, /snap\.licdn\.com/, /zdassets\.com/, /zendesk\.com/, /zopim\.com/],
    beforeSend: (event, hint) => __async(void 0, null, function* () {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
      let currentEvent = event;
      writeDebugLog("Event received in the `beforeSend` hook", {
        currentEvent
      });
      currentEvent = __spreadProps(__spreadValues({}, currentEvent), {
        tags: __spreadProps(__spreadValues({}, currentEvent.tags), {
          source: "error-management"
        })
      });
      if (!currentEvent.user) {
        currentEvent = __spreadProps(__spreadValues({}, currentEvent), {
          user: {
            id: getDebugId()
          }
        });
      }
      if (isAxiosError(hint.originalException)) {
        currentEvent = __spreadProps(__spreadValues({}, currentEvent), {
          tags: __spreadProps(__spreadValues({}, currentEvent.tags), {
            httpStatusCode: (_a = hint.originalException.response) == null ? void 0 : _a.status
          })
        });
      }
      if (((_b = params.filters) == null ? void 0 : _b.blockedGoogleAds) && ((_c = currentEvent.breadcrumbs) == null ? void 0 : _c.some(isBlockedGoogleAdBreadcrumb))) {
        writeDebugLog('Event dropped due to "blockedGoogleAds" filter');
        return null;
      }
      if (((_d = params.filters) == null ? void 0 : _d.xhrWithoutResponse) || ((_e = params.filters) == null ? void 0 : _e.axiosErrorWithoutResponse)) {
        if ((_f = hint.originalException) == null ? void 0 : _f.isAxiosError) {
          const originalException = hint.originalException;
          if (!originalException.response) {
            writeDebugLog('Event dropped due to "axiosErrorWithoutResponse" filter');
            return null;
          }
        }
      }
      if (((_g = params.filters) == null ? void 0 : _g.xhrWithoutResponse) || ((_h = params.filters) == null ? void 0 : _h.xhrWithoutResponseInBreadcrumbs)) {
        if ((_i = currentEvent.breadcrumbs) == null ? void 0 : _i.some(isXhrWithoutResponseBreadcrumb)) {
          writeDebugLog('Event dropped due to "xhrWithoutResponseInBreadcrumbs" filter');
          return null;
        }
      }
      const additionalFilters = (_j = params.filters) == null ? void 0 : _j.additionalFilters;
      if (additionalFilters) {
        currentEvent = yield additionalFilters.reduce((prevEventPromise, filter, currentIndex) => __async(void 0, null, function* () {
          var _a2, _b2;
          const prevEvent = yield prevEventPromise;
          if (!prevEvent) return null;
          const filterInputEvent = {
            traces: prevEvent.breadcrumbs ? prevEvent.breadcrumbs.map(_ref => {
              let {
                category,
                data
              } = _ref;
              return {
                category,
                data
              };
            }) : void 0,
            exception: ((_b2 = (_a2 = prevEvent.exception) == null ? void 0 : _a2.values) == null ? void 0 : _b2.length) ? {
              message: prevEvent.exception.values[0].value
            } : void 0,
            tags: prevEvent.tags || {}
          };
          const filterOutputEvent = yield filter(filterInputEvent);
          if (!filterOutputEvent) {
            writeDebugLog("Event dropped due to custom filter ".concat(currentIndex));
            return null;
          }
          return __spreadProps(__spreadValues({}, prevEvent), {
            tags: __spreadValues(__spreadValues({}, prevEvent.tags), filterOutputEvent.tags)
          });
        }), Promise.resolve(currentEvent));
      }
      if (currentEvent) {
        writeDebugLog("Event with ID ".concat(currentEvent.event_id, " passed all filters and will be sent"));
      }
      return currentEvent;
    }),
    beforeBreadcrumb: breadcrumb => {
      if (breadcrumb.category === "xhr" && breadcrumb.data && /socket.io\/\?EIO/.test(breadcrumb.data.url)) {
        return null;
      }
      if (breadcrumb.category === "console" && breadcrumb.message && /\[HMR\]/.test(breadcrumb.message)) {
        return null;
      }
      return breadcrumb;
    },
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1
  });
  isInitialized = true;
};
var addTrace = trace => {
  if (!isInitialized) return;
  Sentry.addBreadcrumb(trace);
};
var exception = _ref2 => {
  let {
    exception: e,
    tags,
    extra,
    fingerprint
  } = _ref2;
  if (!isInitialized) return;
  Sentry.withScope(scope => {
    scope.setTags(tags || {});
    scope.setExtras(extra || {});
    scope.setFingerprint(fingerprint || ["{{ default }}"]);
    scope.setLevel("fatal");
    scope.setUser({
      id: getDebugId()
    });
    return Sentry.captureException(e);
  });
};
var error_management_default = {
  init: init2,
  addTrace,
  exception
};
export { getDebugId, error_management_default };